import React from "react";
import { ShieldCheck, CheckCircle2, MessagesSquare, Mail } from "lucide-react";

/**
 * Inside Connexis — Consumer experience mockups.
 *
 * Story: a real consumer (Maria L., remortgage shopper) applies on a Connexis
 * brand. The Connexis AI agent qualifies her over SMS, captures the right
 * details, and converts her into a delivered exclusive lead. She gets a
 * branded confirmation email immediately afterwards.
 *
 * Two devices, one flow — buyer prospects can see *exactly* how their inbound
 * leads have been qualified before they ever land in their dashboard.
 *
 * Pure CSS + SVG. Zero third-party assets. No partner names anywhere.
 */
export default function InsidePhoneEmailMockups() {
  return (
    <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
      {/* === LEFT: iPhone SMS — consumer qualification flow === */}
      <div className="flex justify-center lg:justify-end">
        <Iphone>
          <div className="flex items-center justify-between px-4 pt-12 pb-3 border-b border-slate-200">
            <span className="text-[11px] font-semibold text-slate-700 tabular-nums">9:41</span>
            <div className="flex items-center gap-1">
              <SignalIcon /><WifiIcon /><BatteryIcon />
            </div>
          </div>

          {/* Convo header — the consumer sees "Connexis Adviser" */}
          <div className="flex flex-col items-center pt-3 pb-3 border-b border-slate-100">
            <div className="size-12 rounded-full bg-[#0F172A] grid place-items-center text-white text-[11px] font-semibold tracking-wide">CXG</div>
            <div className="mt-1 text-[13px] font-semibold text-slate-900">Connexis Adviser</div>
            <div className="text-[10px] text-slate-500">+44 7480 …</div>
          </div>

          {/* SMS — AI qualifies the consumer, ending in delivery confirmation */}
          <div className="px-3 py-4 space-y-2.5 flex-1 overflow-hidden bg-gradient-to-b from-white to-slate-50/40">
            <DayLabel label="Today · 14:02" />

            <Bubble side="left" time="14:02">
              Hi Maria — thanks for your remortgage enquiry. I&apos;m an adviser at Connexis. Quick question to get you the right deal: is the property in England, Scotland or Wales?
            </Bubble>

            <Bubble side="right" time="14:03">
              England
            </Bubble>

            <Bubble side="left" time="14:03">
              Great. And the outstanding balance, roughly?
            </Bubble>

            <Bubble side="right" time="14:04">
              About £218k, switching off a 4.9% fix
            </Bubble>

            <Bubble side="left" time="14:04">
              Perfect, thank you. I&apos;ll connect you with a qualified broker who can beat that — you&apos;ll get a confirmation email + a call within the hour.
            </Bubble>

            <Bubble side="left" time="14:05">
              Confirmed ✓ — your dedicated adviser is reviewing your details now.
            </Bubble>
          </div>

          {/* iPhone input bar */}
          <div className="border-t border-slate-200 px-3 py-3 flex items-center gap-2 bg-white">
            <div className="size-7 rounded-full bg-slate-100 grid place-items-center text-slate-400 text-sm">+</div>
            <div className="flex-1 h-7 rounded-full border border-slate-200 bg-white" />
            <div className="size-7 rounded-full bg-[#0055FF] grid place-items-center">
              <svg viewBox="0 0 24 24" className="size-3.5 text-white" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </div>
          </div>
        </Iphone>
      </div>

      {/* === RIGHT: Commentary + Email confirmation === */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <div className="size-8 rounded-md bg-[#0055FF]/10 grid place-items-center">
            <MessagesSquare className="size-4 text-[#0055FF]" />
          </div>
          <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Step 1 · AI qualification by SMS</span>
        </div>
        <h3 className="text-2xl font-semibold tracking-tight text-slate-900">Every applicant qualified, on-brand.</h3>
        <p className="mt-3 text-[14.5px] text-slate-600 leading-relaxed">
          The moment someone applies on a Connexis brand, our AI adviser engages them by SMS — capturing affordability, property details, intent and consent in under 60 seconds. By the time a lead reaches your inbox, it&apos;s already been pre-qualified to your sector&apos;s exact criteria.
        </p>
        <ul className="mt-5 space-y-1.5 text-[13.5px] text-slate-700">
          <li className="flex items-center gap-2"><CheckCircle2 className="size-4 text-emerald-500" /> Sub-300ms response · British English · FCA-aware</li>
          <li className="flex items-center gap-2"><CheckCircle2 className="size-4 text-emerald-500" /> Captures qualification + consent in one flow</li>
          <li className="flex items-center gap-2"><CheckCircle2 className="size-4 text-emerald-500" /> &quot;Connexis Adviser&quot; — branded, never a mystery number</li>
        </ul>

        {/* === EMAIL — confirmation to the consumer (Step 2) === */}
        <div className="mt-12">
          <div className="flex items-center gap-2 mb-4">
            <div className="size-8 rounded-md bg-[#0055FF]/10 grid place-items-center">
              <Mail className="size-4 text-[#0055FF]" />
            </div>
            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Step 2 · branded confirmation email</span>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white shadow-float overflow-hidden">
            {/* Browser chrome */}
            <div className="px-3 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
              <span className="size-2.5 rounded-full bg-rose-400" />
              <span className="size-2.5 rounded-full bg-amber-400" />
              <span className="size-2.5 rounded-full bg-emerald-400" />
              <div className="ml-3 flex-1 max-w-[180px] h-5 rounded-md bg-white border border-slate-200 text-[9.5px] text-slate-400 px-2 grid items-center font-mono">
                <span className="truncate">Inbox · Mail</span>
              </div>
            </div>

            {/* Inbox header */}
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2 text-[11px] text-slate-500">
                <Mail className="size-3.5" /> Inbox · Today
              </div>
              <span className="text-[10.5px] text-slate-400 font-mono tabular-nums">14:05 · just now</span>
            </div>

            {/* Email subject */}
            <div className="px-5 py-4 border-b border-slate-100">
              <div className="text-[16px] font-semibold text-slate-900 tracking-tight">Your remortgage enquiry · next steps from Connexis</div>
              <div className="mt-2 flex items-center gap-3 text-[11.5px] text-slate-500">
                <div className="flex items-center gap-2">
                  <div className="size-6 rounded-full bg-[#0F172A] grid place-items-center text-white text-[9px] font-semibold">CX</div>
                  <span><span className="font-medium text-slate-700">Connexis Adviser</span> &lt;ops@connex.is&gt;</span>
                </div>
                <span>to maria.l@…</span>
              </div>
            </div>

            {/* Email body */}
            <div className="px-5 py-6">
              <div className="inline-flex items-center gap-2">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-slate-900 text-white">
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 7h7M4 12h12M4 17h7" strokeLinecap="round" />
                    <circle cx="18" cy="7" r="2.2" fill="#0055FF" stroke="none" />
                    <circle cx="18" cy="17" r="2.2" fill="#0055FF" stroke="none" />
                  </svg>
                </span>
                <span className="text-[15px] font-semibold tracking-tight text-slate-900">Connexis<span className="text-[#0055FF]">.</span></span>
              </div>

              <h4 className="mt-6 text-[18px] font-semibold text-slate-900 tracking-tight leading-snug">Hi Maria — we&apos;ve received your remortgage enquiry.</h4>
              <p className="mt-3 text-[13.5px] text-slate-600 leading-relaxed">
                Thanks for completing your details. Based on what you&apos;ve told us, a dedicated mortgage adviser is reviewing your file now and will be in touch within the hour to talk through the best rates available.
              </p>

              <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50/60 p-4">
                <div className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500">Your enquiry summary</div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-[12px]">
                  <Field l="Property" v="England" />
                  <Field l="Outstanding" v="£218,000" />
                  <Field l="Current rate" v="4.9% fix" />
                  <Field l="Looking for" v="Remortgage" />
                </div>
              </div>

              <p className="mt-5 text-[13.5px] text-slate-600 leading-relaxed">
                If you have any questions before your adviser calls, simply reply to this email or message us back on the number you spoke to.
              </p>

              <div className="mt-7 pt-4 border-t border-slate-100 text-[11px] text-slate-500 leading-relaxed flex items-start gap-2">
                <ShieldCheck className="size-3.5 text-slate-400 mt-0.5 shrink-0" />
                <span>Connexis Group Ltd · Company no. 17214446 · 71-75 Shelton Street, Covent Garden, London WC2H 9JQ · FCA-aware advisory flow. We protect your data and never share it outside of the broker you&apos;ve been matched with.</span>
              </div>
            </div>
          </div>

          <p className="mt-5 text-[13px] text-slate-600 leading-relaxed">
            By the time this enquiry reaches your buyer dashboard, the consumer has already had a branded conversation, given explicit consent, and been told to expect your call. <span className="font-medium text-slate-900">Pickup rates triple. Cold-call anxiety disappears.</span>
          </p>
        </div>
      </div>
    </div>
  );
}

// ===== iPhone frame =====
function Iphone({ children }) {
  return (
    <div className="relative w-[280px] sm:w-[300px] aspect-[9/19] rounded-[44px] bg-slate-900 p-[10px] shadow-[0_30px_60px_-15px_rgba(15,23,42,0.4),0_8px_25px_-5px_rgba(15,23,42,0.2)]" data-testid="iphone-frame">
      <div className="absolute inset-0 rounded-[44px] ring-1 ring-white/10 pointer-events-none" />
      <div className="relative h-full w-full rounded-[34px] bg-white overflow-hidden flex flex-col">
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-[90px] h-[26px] rounded-full bg-slate-900 z-30" />
        {children}
      </div>
    </div>
  );
}

function Bubble({ side, time, children }) {
  const isLeft = side === "left";
  return (
    <div className={`flex ${isLeft ? "justify-start" : "justify-end"}`}>
      <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-[11.5px] leading-snug ${isLeft ? "bg-slate-100 text-slate-800 rounded-bl-md" : "bg-[#0055FF] text-white rounded-br-md"}`}>
        {children}
        <div className={`mt-1 text-[9px] tabular-nums ${isLeft ? "text-slate-400" : "text-white/70"}`}>{time}</div>
      </div>
    </div>
  );
}

function DayLabel({ label }) {
  return (
    <div className="flex justify-center">
      <span className="text-[9.5px] font-medium text-slate-400 tracking-wide">{label}</span>
    </div>
  );
}

function Field({ l, v }) {
  return (
    <div className="rounded-md bg-white border border-slate-200 px-2.5 py-1.5">
      <div className="text-[9px] font-semibold uppercase tracking-[0.1em] text-slate-500">{l}</div>
      <div className="text-[12px] font-medium text-slate-900 tabular-nums">{v}</div>
    </div>
  );
}

// Status-bar icons
function SignalIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-3.5 text-slate-900" fill="currentColor"><rect x="2" y="14" width="3" height="6" rx="0.5" /><rect x="7" y="10" width="3" height="10" rx="0.5" /><rect x="12" y="6" width="3" height="14" rx="0.5" /><rect x="17" y="2" width="3" height="18" rx="0.5" /></svg>
  );
}
function WifiIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-3.5 text-slate-900" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 9c5.5-5.5 14.5-5.5 20 0M5 12.5c3.5-3.5 10.5-3.5 14 0M8.5 16c1.5-1.5 5.5-1.5 7 0" strokeLinecap="round" /><circle cx="12" cy="19.5" r="1" fill="currentColor" /></svg>
  );
}
function BatteryIcon() {
  return (
    <svg viewBox="0 0 30 14" className="w-6 h-3 text-slate-900"><rect x="0.5" y="0.5" width="25" height="13" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1" /><rect x="2" y="2" width="22" height="10" rx="1" fill="currentColor" /><rect x="26" y="4" width="2.5" height="6" rx="1" fill="currentColor" /></svg>
  );
}
